import React from 'react';
import cn from 'classnames';

import Typography from 'widget/shared/ui/Typography';
import {
  TypographySize,
  TypographyColor,
  TypographyTag
} from 'widget/shared/ui/Typography/types';
import CheckIcon from 'widget/shared/icons/CheckIcon';
import { IconColor, IconSize } from 'widget/shared/icons/BaseIcon';
import { WidgetTexts } from 'widget/consts/texts';

import styles from './Dropdown.module.scss';

/** Максимальная высота дропдауна (совпадает с max-height в SCSS) */
const DROPDOWN_MAX_HEIGHT = 280;
/** Зазор между триггером и дропдауном */
const DROPDOWN_GAP = 4;

export type DropdownOption = {
  value: string;
  label: string;
  secondaryLabel?: string;
};

export type DropdownProps = {
  options: DropdownOption[];
  value?: string;
  isOpen: boolean;
  onClose: () => void;
  onSelect: (value: string) => void;
  triggerRef: React.RefObject<HTMLElement | null>;
  searchable?: boolean;
  searchPlaceholder?: string;
  emptyText?: string;
  fontSize?: TypographySize;
  fontColor?: TypographyColor;
  secondaryFontColor?: TypographyColor;
  backgroundColor?: string;
  className?: string;
};

const Dropdown: React.FC<DropdownProps> = ({
  options,
  value,
  isOpen,
  onClose,
  onSelect,
  triggerRef,
  searchable = true,
  searchPlaceholder = WidgetTexts.shared.dropdown.searchPlaceholder,
  emptyText = WidgetTexts.shared.dropdown.emptyText,
  fontSize = TypographySize.s,
  fontColor = TypographyColor.primary,
  secondaryFontColor = TypographyColor.secondary,
  backgroundColor,
  className
}) => {
  const [searchQuery, setSearchQuery] = React.useState('');
  const [placement, setPlacement] = React.useState<'bottom' | 'top'>('bottom');
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  // Сброс поиска при закрытии
  React.useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
    }
  }, [isOpen]);

  // Определение позиции (снизу или сверху) при открытии
  React.useLayoutEffect(() => {
    if (!isOpen || !triggerRef.current) {
      return;
    }

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - triggerRect.bottom - DROPDOWN_GAP;
    const spaceAbove = triggerRect.top - DROPDOWN_GAP;

    if (spaceBelow >= DROPDOWN_MAX_HEIGHT) {
      setPlacement('bottom');
    } else if (spaceAbove >= DROPDOWN_MAX_HEIGHT) {
      setPlacement('top');
    } else {
      // Ни сверху, ни снизу не хватает — показываем снизу
      setPlacement('bottom');
    }
  }, [isOpen, triggerRef]);

  // Закрытие при клике вне области
  React.useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(target) &&
        triggerRef.current &&
        !triggerRef.current.contains(target)
      ) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose, triggerRef]);

  // Автофокус на поле поиска при открытии
  React.useEffect(() => {
    if (isOpen && searchable && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen, searchable]);

  const filteredOptions = React.useMemo(() => {
    if (!searchQuery.trim()) {
      return options;
    }
    const query = searchQuery.toLowerCase();
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(query) ||
        option.value.toLowerCase().includes(query) ||
        option.secondaryLabel?.toLowerCase().includes(query)
    );
  }, [options, searchQuery]);

  const handleSearchChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(event.target.value);
    },
    []
  );

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Escape') {
        onClose();
      }
    },
    [onClose]
  );

  if (!isOpen) {
    return null;
  }

  const containerStyle = backgroundColor ? { backgroundColor } : undefined;

  return (
    <div
      ref={dropdownRef}
      className={cn(
        styles.dropdown,
        placement === 'top' && styles.dropdown_top,
        className
      )}
      style={containerStyle}
    >
      {searchable && (
        <div className={styles.dropdown__search}>
          <input
            ref={searchInputRef}
            type="text"
            className={styles['dropdown__search-input']}
            value={searchQuery}
            onChange={handleSearchChange}
            onKeyDown={handleKeyDown}
            placeholder={searchPlaceholder}
          />
        </div>
      )}
      <div className={styles.dropdown__list}>
        {filteredOptions.length === 0 ? (
          <Typography
            size={fontSize}
            color={secondaryFontColor}
            tag={TypographyTag.div}
            className={styles.dropdown__empty}
          >
            {emptyText}
          </Typography>
        ) : (
          filteredOptions.map((option) => {
            const isSelected = option.value === value;
            return (
              <div
                key={option.value}
                className={cn(
                  styles['dropdown__item'],
                  isSelected && styles['dropdown__item_selected']
                )}
                onClick={() => onSelect(option.value)}
              >
                <Typography
                  size={fontSize}
                  color={fontColor}
                  tag={TypographyTag.span}
                  className={cn(
                    styles['dropdown__item-label'],
                    isSelected && styles['dropdown__item-label_selected']
                  )}
                >
                  {option.label}
                </Typography>
                {option.secondaryLabel && (
                  <Typography
                    size={fontSize}
                    color={secondaryFontColor}
                    tag={TypographyTag.span}
                    className={cn(
                      styles['dropdown__item-secondary'],
                      isSelected && styles['dropdown__item-secondary_selected']
                    )}
                  >
                    {option.secondaryLabel}
                  </Typography>
                )}
                {isSelected && (
                  <CheckIcon
                    size={IconSize.XS}
                    color={IconColor.Main}
                    className={styles['dropdown__item-check']}
                  />
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default React.memo(Dropdown);
